<template>
  <ion-item
    class="report-entry-item"
    detail
    button
    lines="full"
    @click="navigateToReport(entry.id, entry.connection)">

    <div class="report-entry-start" slot="start">
      <template v-if="entry.connection === true && entry.reports != null">
        <template v-for="(connectedHorseEntries, connectedHorse) in entry.reports" :key="connectedHorse">
          <h3 class="fl-s1 wl-light multi-span-text f-mono">
            <template v-for="(connectedTimestamp, timestampIndex) in mapToPropertyUnique(connectedHorseEntries, 'timestamp')" :key="timestampIndex">
              <span class="" v-if="timestampToDateTime(connectedTimestamp)">{{ timestampToDateTime(connectedTimestamp) || i18n.$t('tracking.no_timestamp') }}</span>
            </template>
          </h3>
          </template>
      </template>
      
      <template v-else>
          <h3 class="fl-s1 wl-light f-mono">{{ timestampToDateTime(entry.timestamp) || i18n.$t('tracking.no_timestamp') }}</h3>
      </template>
    </div>

    <ion-label>
      <template v-if="entry.connection === true && entry.reports != null">
        <template v-for="(connectedHorseEntries, connectedHorse) in entry.reports" :key="connectedHorse">
          <!-- If the rare case happens that more than one horse is assigned in a connection, show which reports belong to which horse --> <!-- TODO Test if this case works! -->
          <h1 class="fl-0 wl-semibold" v-if="Object.keys(entry.reports).length > 1">{{ (connectedHorse != 'null') ? getHorseName(connectedHorse) : i18n.$t('tracking.horse_not_assigned') }}</h1>

          <p class="fl-s1 wl-semibold wrap text-with-badge multi-span-text">
            <ion-badge>{{ connectedHorseEntries.length }}<ion-icon :icon="documentTextOutline"></ion-icon></ion-badge>
            <!-- Unique by descriptor as this is what the user sees -->
            <span v-for="(connectedType, typeIndex) in mapToPropertyUnique(connectedHorseEntries, 'type', 'descriptor')" :key="typeIndex">
              {{ getLocalizedReportTypeName(connectedType) }}
            </span>
          </p>

          <p class="fl-s1 wrap text-with-icon multi-span-text" v-if="mergeAnalyses(connectedHorseEntries).length">
            <AnimatedLogo name="cycle" stopPattern="cross" class="logo inline-icon" duration="6000" :stop="!areAnyAnalysesUncompleted(mergeAnalyses(connectedHorseEntries))"></AnimatedLogo>
            {{ i18n.$t('tracking.analyses.title') }} &mdash;
            <span v-for="(analysisCount, statusKey) in countAnalyses(mergeAnalyses(connectedHorseEntries))" :key="statusKey">
              {{ i18n.$t(`tracking.analyses.${statusKey}`) }}: {{ analysisCount }}
            </span>
          </p>

          <h3 class="fl-s1 wl-light multi-span-text">
            <template v-for="(connectedType, typeIndex) in mapToPropertyUnique(connectedHorseEntries, 'type', 'main_category')" :key="typeIndex">
              <span v-if="getLocalizedMainCategory(connectedType)">{{ getLocalizedMainCategory(connectedType) }}</span>
            </template>
          </h3>
        </template>
      </template>
      
      <template v-else>
        <p class="fl-s1 wl-semibold wrap">{{ getLocalizedReportTypeName(entry.type) }}</p>
        <p class="fl-s1 wrap text-with-icon multi-span-text" v-if="entry.analyses != null && entry.analyses.length">
          <AnimatedLogo name="cycle" stopPattern="cross" class="logo inline-icon" duration="6000" :stop="!areAnyAnalysesUncompleted(entry.analyses)"></AnimatedLogo>
          {{ i18n.$t('tracking.analyses.title') }} &mdash;
          <span v-for="(analysisCount, statusKey) in countAnalyses(entry.analyses)" :key="statusKey">
            {{ i18n.$t(`tracking.analyses.${statusKey}`) }}:&nbsp;{{ analysisCount }}
          </span>
        </p>
        <h3 class="fl-s1 wl-light">{{ getLocalizedMainCategory(entry.type) }}</h3>
      </template>
    </ion-label>

    <!-- TODO Use same check as being uploaded as ViewReport? Then it would only show the status while still being uploaded! -->
    <ion-button 
      v-if="entry.uploadStatus != null && getUploadStatus(entry.uploadStatus.key) != null"
      :key="getUploadStatus(entry.uploadStatus.key).design.identifier"
      shape="round"
      fill="clear"
      class="upload-progress-button"
      :color="getUploadStatus(entry.uploadStatus.key).design.shortColor"
      @click.stop="showUploadStatus()"> <!--TODO Maybe add green pencil icon to reports that are being updated, also in upload overview, and also maybe on already updated ones -->
      <CircularProgress
        class="upload-progress"
        :style="'--color: var(' + getUploadStatus(entry.uploadStatus.key).design.color + ');'"
        :progress="(getUploadStatus(entry.uploadStatus.key).status.progress >= 0) ? getUploadStatus(entry.uploadStatus.key).status.progress : 0">
        <ion-icon :icon="getUploadStatus(entry.uploadStatus.key).design.icon"></ion-icon>
      </CircularProgress>
    </ion-button> <!-- TODO Maybe add icon to indicate cached reports? -->
  </ion-item>
</template>

<script>
import { IonItem, IonLabel, IonIcon, IonButton, IonBadge, menuController } from '@ionic/vue';
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

import { documentTextOutline } from 'ionicons/icons';

import AnimatedLogo from '@/components/AnimatedLogo.vue';
import CircularProgress from '@/components/CircularProgress.vue';

import { useI18n } from "@/utils/i18n";

import { useDayjs } from '@/utils/dayjs';

import { openUploadStatusModal, default as uploadModalComponent } from '@/components/UploadStatusModal.vue';

import _ from 'lodash';

export default  {
  name: 'ReportTrackingItem',
  components: { IonItem, IonLabel, IonIcon, IonButton, IonBadge, CircularProgress, AnimatedLogo },
  props: {
    entry: Object
  },
  setup() {
    const i18n = useI18n();

    const store = useStore();

    const router = useRouter();

    const { timestampToLocalDateReducedPrecision } = useDayjs();

    const timestampToDateTime = computed(() => {
      return (timestamp) => {
        let localDateTime = timestampToLocalDateReducedPrecision.value(timestamp);
        if (localDateTime != null) {
          return localDateTime.format('HH:mm');
        }
        return null;
      }
    });

    const getLocalizedMainCategoryById = function(mainCategoryId){
      let categoryNames = store.getters['reports/getMainCategoryNamesById'](mainCategoryId);
      if (categoryNames) {
        return categoryNames[i18n.locale.value];
      } else {
        return null;
      }
    }

    //Returns the report type if it already is a full object, otherwise tries to get it from store, if it is just an ID
    const getReportType = function(reportTypeOrID) {
      if (reportTypeOrID != null) {
        if (_.isObject(reportTypeOrID)) {
          return reportTypeOrID;
        } else {
          return store.getters['reports/getReportTypesByIdentifiers'](reportTypeOrID);
        }
        
      }
    }

    const getLocalizedMainCategory = function(reportTypeOrID){
      let reportType = getReportType(reportTypeOrID);
      if (reportType != null && reportType['main_category'] != null) {
        return getLocalizedMainCategoryById(reportType['main_category']);
      }
      return null;
    }

    const getLocalizedReportTypeNameByDescriptor = function(descriptor){
      if (descriptor) {
        return descriptor; //TODO Use the translated name here. Translate in API definition in reports too!
      } else { //TODO Can get it directly from each report
        return null;
      }
    }

    const getLocalizedReportTypeName = function(reportTypeOrID){
      let reportType = getReportType(reportTypeOrID);
      if (reportType != null && reportType.descriptor != null) {
        return getLocalizedReportTypeNameByDescriptor(reportType.descriptor);
      } else {
        return null;
      }
    }

    const getHorseName = function(horseId){
      let horse = store.getters['horses/getPersonalInfoById'](horseId);
      if (horse) {
        return horse.name;
      } else {
        return null;
      }
    }

    const mergeAnalyses = function(reports) {
      let analyses = [];
      for (const report of Object.values(reports)) {
        if (report.analyses != null && Array.isArray(report.analyses) && report.analyses.length) {
          for (let newAnalysis of report.analyses) analyses.push(newAnalysis);
        }
      }
      return analyses;
    }

    const countAnalyses = function(analyses) {
      if (analyses == null) return null;
      return _.countBy(analyses, (analysis) => {
        if (analysis != null && analysis.status != null && analysis.status.completed) {
          return 'completed';
        } else if (analysis != null && analysis.status != null && analysis.status.queued) {
          return 'queued';
        } else {
          return 'unqueued';
        }
      });
    }

    const areAnyAnalysesUncompleted = function(analyses) {
      if (analyses == null) return null;
      return _.some(analyses, (analysis) => {
        if (analysis != null && analysis.status != null) {
          if (analysis.status.completed) return false;
        }
        return true;
      });
    }

    const navigateToReport = function(reportOrConnectionId, isConnection){
      let newRoute;
      if (isConnection === true) {
        newRoute = {name: 'view-connected-reports', params: { connection_id: reportOrConnectionId }};
      } else {
        newRoute = {name: 'view-report', params: { id: reportOrConnectionId }};
      }

      let currentRoute = router.currentRoute.value;
      menuController.close('side-component');
      //If we are already navigated to a report route, replace it, else push it
      if (currentRoute.name === 'view-report' || currentRoute.name === 'view-connected-reports') {
        router.replace(newRoute);
      } else {
        router.push(newRoute);
      }
    }

    const mapToPropertyUnique = function(array, property, uniqueBy) {
      //Return empty array if invalid
      if (array == null || property == null) return [];
      //Othwerwise map each entry in the array to the given property and return a unique array
      const mappedArray = _.map(array, property);
      //If a uniqueBy property is given, check uniqueness by that parameter
      if (uniqueBy != null) {
        return _.uniqBy(mappedArray, uniqueBy);
      } else { //Otherwise check whole entry for uniqueness
        return _.uniq(mappedArray);
      }
    }

    const getUploadStatusDesign = function(status){
      return store.getters['getUploadStatusDesign'](status);
    }

    const processStatus = function(status) {
      return _.keyBy(_.map((status || []), (statusObject) => {
        let originalStatus = (statusObject != null) ? statusObject.status : null;
        let mappedStatus = {
          ..._.pick(statusObject, ['key', 'index']),
          status: originalStatus,
          design: getUploadStatusDesign(originalStatus)
        };
        return mappedStatus;
      }), 'key');
    }

    //Get all statuses and map them into their displayed texts and design. Mapped to object with the keys being the upload status keys
    const reportUploadStatus = computed(() => processStatus(store.getters['reports/getUploadStatus']));
    const reportConnectionUploadStatus = computed(() => processStatus(store.getters['reports/getConnectionUploadStatus']));

    //Returns a function to get the upload status for the given key, if it exists
    const getUploadStatus = computed(() => (key) => {
      if (reportUploadStatus.value != null && key != null) {
        //Check both report and connection for this uploding key
        if (key in reportUploadStatus.value) return reportUploadStatus.value[key];
        if (key in reportConnectionUploadStatus.value) return reportConnectionUploadStatus.value[key];
      }
    });

    const showUploadStatus = function(){
      openUploadStatusModal(uploadModalComponent);
    }

    return {
      i18n,
      router,
      store,
      timestampToDateTime,
      getLocalizedMainCategory,
      getLocalizedReportTypeName,
      getHorseName,
      mergeAnalyses,
      countAnalyses,
      areAnyAnalysesUncompleted,
      navigateToReport,
      getUploadStatus,
      getUploadStatusDesign,
      showUploadStatus,
      mapToPropertyUnique,
      documentTextOutline
    };
  }
}
</script>

<style scoped>
.report-entry-start {
  margin-inline: 6px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  
}

.report-entry-item {
  --padding-start: 0px;
  --inner-padding-start: 0px;
  --inner-padding-end: 0px;
}

.report-entry-item ion-label {
  padding-inline-start: 12px;
  padding-inline-end: 6px;
  border-inline-start: 1px solid var(--ion-item-border-color);
  margin-inline-start: 6px;
}

.report-entry-item:last-of-type {
  --border-width: 0px;
}

.wrap {
  text-overflow: unset;
  height: auto;
  white-space: normal;
}

.upload-progress-button {
  height: 2.5em;
  width: 2.5em;
  --padding-bottom: 0px;
  --padding-top: 0px;
  --padding-start: 0px;
  --padding-end: 0px;
  margin: 0px;
  font-size: inherit;
}

.upload-progress {
  width: 2.5em;
  height: 2.5em;
}

.text-with-badge {
  margin-top: 5px;
  margin-bottom: 5px;
}

.text-with-badge ion-badge {
  vertical-align: bottom;
  margin-right: 5px;
  --background: var(--ion-text-color);
  --color: var(--ion-text-color-contrast);
}

p {
  color: var(--ion-text-color)!important;
}

p.wrap {
  margin-bottom: 0px;
}

.text-with-icon {
  line-height: 32px;
}

.text-with-icon ion-icon {
  vertical-align: -0.2em;
  margin-right: 5px;
  color: var(--ion-color-primary-text);
}

.text-with-icon .logo {
  vertical-align: -0.22em;
  margin-right: 5px;
  font-size: 1.1rem;
  color: var(--ion-color-primary-text);
}

ion-badge ion-icon {
  margin-left: 2px;
  vertical-align: bottom;
}

.multi-span-text span:not(:last-of-type)::after {
  content: " | ";
  font-weight: var(--weight-level-light);
}


</style>